import type { CheckSendCountryItem, CheckSendLogisticsItem } from './types'
import type { SassmallGoodsPostCalRequest } from '~/sassmall/api/checkSendPackage'
import { ElMessage } from 'element-plus'
import { computed, ref } from 'vue'
import {
  checkSassmallGoodsPostable,
  listSassmallCheckSendCountries,
} from '~/sassmall/api/checkSendPackage'
import { getSassmallTenantDomain } from '~/sassmall/storage'
import { st } from '~/sassmall/i18n'

interface CheckSendAvailabilityParams {
  countryCode: string
  warehouseCode?: string | null
  goodCode?: string | number | null
  goodType?: string | null
  weight?: string | number | null
  productName?: string | null
  category?: string | null
  skuText?: string | null
  skuInfo?: Array<Record<string, any>>
  showError?: boolean
  // 保留旧组件入参，迁移后由后端基于商品名称/品类统一识别，不再调用旧运输分类接口。
  matchTransportGoods?: boolean
  transportCategoryCode?: string | number | null
  transportCategoryName?: string | null
  transportCategoryPath?: string | null
}

interface CheckSendAvailabilityResult {
  isAllowed: boolean
  list: CheckSendLogisticsItem[]
  matchedBy: Array<'goodCode' | 'goodType' | 'productName'>
}

interface LoadCountryListOptions {
  force?: boolean
  showError?: boolean
  warehouseCode?: string
}

const DEFAULT_WEIGHT = '1'
const countryListCache = new Map<string, CheckSendCountryItem[]>()
const countryListPendingMap = new Map<string, Promise<CheckSendCountryItem[]>>()
const availabilityCache = new Map<string, CheckSendAvailabilityResult>()

function normalizeText(value: any, maxLength = Number.POSITIVE_INFINITY) {
  return String(value || '').trim().slice(0, maxLength)
}

function normalizeGoodType(raw?: string | null) {
  const text = normalizeText(raw, 100)
  return text ? text.split('/')[0].trim() : ''
}

function currentScope() {
  return getSassmallTenantDomain() || 'default'
}

function countryCacheKey(warehouseCode = '') {
  return `${currentScope()}::${normalizeText(warehouseCode) || 'all'}`
}

function buildAvailabilityCacheKey(params: CheckSendAvailabilityParams) {
  return [
    currentScope(),
    normalizeText(params.warehouseCode),
    normalizeText(params.countryCode),
    normalizeText(params.goodCode),
    normalizeGoodType(params.goodType),
    normalizeText(params.weight || DEFAULT_WEIGHT),
    normalizeText(params.productName, 500),
    normalizeText(params.category, 200),
    normalizeText(params.skuText, 1000),
  ].join('::')
}

function routeIdentity(item: any) {
  return [
    item?.id,
    item?.warehouseCode,
    item?.countryCode,
    item?.cargoType,
    item?.freightType,
    item?.productName,
  ].map(value => String(value || '').trim()).join('::')
}

function normalizeLogistics(list: any[]): CheckSendLogisticsItem[] {
  const deduped = list.filter((entry, index, source) => {
    const identity = routeIdentity(entry)
    return source.findIndex(current => routeIdentity(current) === identity) === index
  })

  return deduped.map((entry, index) => ({
    ...entry,
    id: String(entry?.id || routeIdentity(entry) || `route-${index}`),
  }))
}

function isAllowedLogistics(item: any) {
  return item?.isAllow === undefined || item?.isAllow === null || Number(item.isAllow) === 1
}

function countryDisplayName(code: string, locale: string) {
  const normalizedCode = normalizeText(code)
  if (!normalizedCode)
    return ''
  try {
    const DisplayNames = (Intl as any).DisplayNames
    return DisplayNames
      ? new DisplayNames([locale || 'zh-CN'], { type: 'region' }).of(normalizedCode.toUpperCase()) || normalizedCode
      : normalizedCode
  }
  catch {
    return normalizedCode
  }
}

export function useCheckSendAvailability() {
  const { locale } = useI18n()
  const loadingCountries = ref(false)
  const querying = ref(false)
  const countries = ref<CheckSendCountryItem[]>([])
  const transfersMap = ref<Record<string, string>>({})
  // 兼容原卡片的监听字段；值改为租户域名，切换租户时会自动重查。
  const shopId = computed(() => currentScope())

  const countryOptions = computed(() => countries.value.map((item) => {
    const code = normalizeText(item.code || item.value)
    return {
      ...item,
      value: code,
      label: countryDisplayName(code, String(locale.value || 'zh-CN')),
    }
  }))

  function mergeTransfersMap(nextMap?: Record<string, string>) {
    if (!nextMap || typeof nextMap !== 'object')
      return
    transfersMap.value = { ...transfersMap.value, ...nextMap }
  }

  async function loadCountryList(options: LoadCountryListOptions = {}) {
    const warehouseCode = normalizeText(options.warehouseCode)
    const cacheKey = countryCacheKey(warehouseCode)

    if (!options.force && countryListCache.has(cacheKey)) {
      const cached = countryListCache.get(cacheKey) || []
      countries.value = cached
      return cached
    }
    if (!options.force && countryListPendingMap.has(cacheKey)) {
      const pending = await countryListPendingMap.get(cacheKey)!
      countries.value = pending
      return pending
    }

    loadingCountries.value = true
    const pendingPromise = listSassmallCheckSendCountries(warehouseCode, String(locale.value || 'zh-CN'))
      .then((list) => {
        const normalizedList = Array.isArray(list) ? list : []
        countryListCache.set(cacheKey, normalizedList)
        countries.value = normalizedList
        return normalizedList
      })
      .catch((error: any) => {
        if (options.showError !== false) {
          ElMessage.error(error?.message || st('目的地加载失败'))
        }
        return []
      })
      .finally(() => {
        countryListPendingMap.delete(cacheKey)
        loadingCountries.value = false
      })

    countryListPendingMap.set(cacheKey, pendingPromise)
    return await pendingPromise
  }

  async function queryAvailability(params: CheckSendAvailabilityParams): Promise<CheckSendAvailabilityResult> {
    const countryCode = normalizeText(params.countryCode, 64)
    const warehouseCode = normalizeText(params.warehouseCode, 64)
    const goodCode = normalizeText(params.goodCode, 100)
    const goodType = normalizeGoodType(
      params.goodType
      || params.transportCategoryName
      || params.transportCategoryPath,
    )
    const productName = normalizeText(params.productName, 500)
    const category = normalizeText(
      params.category
      || params.transportCategoryName
      || params.transportCategoryPath
      || params.goodType,
      200,
    )
    const skuText = normalizeText(params.skuText, 1000)
    const weight = normalizeText(params.weight || DEFAULT_WEIGHT)

    if (!countryCode) {
      const message = st('请选择目的地')
      if (params.showError !== false)
        ElMessage.warning(message)
      throw new Error(message)
    }
    if (!goodType && !productName) {
      const message = st('请至少传入商品名称或商品分类')
      if (params.showError !== false)
        ElMessage.warning(message)
      throw new Error(message)
    }

    const cacheKey = buildAvailabilityCacheKey({
      ...params,
      warehouseCode,
      countryCode,
      goodCode,
      goodType,
      productName,
      category,
      skuText,
      weight,
    })
    if (availabilityCache.has(cacheKey))
      return availabilityCache.get(cacheKey)!

    const payload: SassmallGoodsPostCalRequest = {
      warehouseCode,
      countryCode,
      cargoTypeCode: '',
      goodType,
      goodCode,
      weight,
      productName,
      category,
      skuText,
      skuInfo: params.skuInfo,
    }

    querying.value = true
    try {
      const routes = await checkSassmallGoodsPostable(payload)
      const list = normalizeLogistics(routes)
      const result: CheckSendAvailabilityResult = {
        isAllowed: list.some(isAllowedLogistics),
        list,
        matchedBy: [
          ...(goodCode ? ['goodCode' as const] : []),
          ...(goodType ? ['goodType' as const] : []),
          ...(productName ? ['productName' as const] : []),
        ],
      }
      availabilityCache.set(cacheKey, result)
      return result
    }
    catch (error: any) {
      if (params.showError !== false) {
        ElMessage.error(error?.message || st('可寄结果查询失败'))
      }
      throw error
    }
    finally {
      querying.value = false
    }
  }

  return {
    countries,
    countryOptions,
    loadingCountries,
    querying,
    shopId,
    transfersMap,
    mergeTransfersMap,
    loadCountryList,
    queryAvailability,
  }
}

export type {
  CheckSendAvailabilityParams,
  CheckSendAvailabilityResult,
  LoadCountryListOptions,
}
